// Tiny React hook exposing the bus's current connection state to the UI.
// Source of truth is the bus itself — we just subscribe and re-render.
//
// Why this matters: without a visible connection pill, a silently-closed
// socket leaves the agent staring at a stale monitor wondering why nothing
// updates. Showing the state plus a Reconnect button gives a recovery path
// that doesn't require logging out.

import { useEffect, useState } from 'react';
import { getBus, type ConnectionState } from '@prakash-software/shared';

export function useBusConnection(): ConnectionState {
  const [state, setState] = useState<ConnectionState>(() => {
    try {
      return getBus().getConnectionState();
    } catch {
      return 'closed';
    }
  });
  useEffect(() => {
    let bus: ReturnType<typeof getBus>;
    try {
      bus = getBus();
    } catch {
      return;
    }
    setState(bus.getConnectionState());
    return bus.onConnectionChange(setState);
  }, []);
  return state;
}

/**
 * Subscribe to connection-state changes outside React. Returns an unsubscribe.
 * No-op (and a no-op unsubscribe) when the bus isn't configured yet.
 */
export function onBusConnectionChange(
  listener: (state: ConnectionState) => void,
): () => void {
  try {
    return getBus().onConnectionChange(listener);
  } catch {
    return () => {};
  }
}

export function reconnectBus(): void {
  try {
    getBus().reconnect();
  } catch {
    /* bus not configured */
  }
}
